import React, { useCallback, useState } from 'react';
import { FeedbackCategory, sendSummaryFeedback, UserFeedback } from './helper';
import { useLogging } from '@ecosystem/newron-client-lib';
import { actions, elements, overlays } from '@ecosystem/newron-design-system';
import { FeedbackModal } from './FeedbackModal';

const { IconButton } = actions;
const { Toast } = overlays;

interface AiFeedbackProps {
  endpoint: string;
  requestId: string;
}

export const AiFeedback: React.FC<AiFeedbackProps> = ({ endpoint, requestId }) => {
  const { error: logError } = useLogging();
  const [toast, setToast] = useState<{ type: overlays.Notification; message: string }>();
  const [hasSubmittedFeedback, setHasSubmittedFeedback] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

  const handlePositiveFeedback = useCallback(async () => {
    try {
      await sendSummaryFeedback(endpoint, requestId, { feedback: UserFeedback.LIKE });
      setToast({ message: 'Feedback successfully submitted.', type: 'success' });
      setHasSubmittedFeedback(true);
    } catch (error) {
      logError(error, { action: 'failed to send positive insight summary feedback' });
      setToast({ message: 'Failed to send feedback.', type: 'error' });
    }
  }, [endpoint, requestId, logError]);

  const handleNegativeFeedback = useCallback(
    async (feedbackCategory: FeedbackCategory, comment?: string) => {
      try {
        await sendSummaryFeedback(endpoint, requestId, { feedback: UserFeedback.DISLIKE, feedbackCategory, comment });
        setToast({ message: 'Feedback successfully submitted.', type: 'success' });
        setHasSubmittedFeedback(true);
      } catch (error) {
        logError(error, { action: 'failed to send negative insight summary feedback' });
        setToast({ message: 'Failed to send feedback.', type: 'error' });
      } finally {
        setIsFeedbackModalOpen(false);
      }
    },
    [endpoint, requestId]
  );

  return (
    <>
      <div className="flex r-0">
        <IconButton onClick={handlePositiveFeedback} disabled={hasSubmittedFeedback}>
          <elements.Icon.icons.ThumbsUp title="Like" />
        </IconButton>
        <IconButton onClick={() => setIsFeedbackModalOpen(true)} disabled={hasSubmittedFeedback}>
          <elements.Icon.icons.ThumbsDown title="Dislike" />
        </IconButton>
      </div>
      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        onClose={() => setIsFeedbackModalOpen(false)}
        handleApply={handleNegativeFeedback}
      />
      {toast && (
        <Toast type={toast.type} onComplete={() => setToast(undefined)}>
          {toast.message}
        </Toast>
      )}
    </>
  );
};
